import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { typeLabel } from "@/lib/constants";
import { Plus, FolderKanban, ClipboardList, ShieldCheck, Clock, MessagesSquare, Check, Building2, Flag, AlertTriangle } from "lucide-react";

const statusLabels = {
    planning: "Planning",
    fieldwork: "Fieldwork",
    awaiting_review: "Awaiting review",
    review_points: "Review queries to address",
    partner_review: "Partner review",
    approved: "Approved — awaiting final",
    awaiting_client_approval: "Awaiting client approval",
    finalised: "Finalised",
};
// Pipeline order + accent bar colour for each stage.
const PIPELINE = [
    ["planning", "bg-blue-500"],
    ["fieldwork", "bg-amber-500"],
    ["awaiting_review", "bg-amber-600"],
    ["review_points", "bg-rose-500"],
    ["partner_review", "bg-indigo-500"],
    ["approved", "bg-teal-500"],
    ["awaiting_client_approval", "bg-violet-500"],
    ["finalised", "bg-emerald-500"],
];
const statusStyles = {
    planning: "bg-blue-50 text-blue-800",
    fieldwork: "bg-amber-50 text-amber-800",
    awaiting_review: "bg-amber-50 text-amber-800",
    review_points: "bg-rose-50 text-rose-800",
    partner_review: "bg-indigo-50 text-indigo-800",
    approved: "bg-teal-50 text-teal-800",
    awaiting_client_approval: "bg-violet-50 text-violet-800",
    finalised: "bg-emerald-50 text-emerald-800",
};

export default function Dashboard() {
    const [engagements, setEngagements] = useState([]);
    const [attention, setAttention] = useState([]);

    useEffect(() => { api.get("/engagements").then((r) => setEngagements(r.data)).catch(() => {}); }, []);
    useEffect(() => { api.get("/my/attention").then((r) => setAttention(r.data)).catch(() => {}); }, []);

    const stats = {
        total: engagements.length,
        active: engagements.filter((e) => e.status !== "finalised").length,
        review: engagements.filter((e) => ["awaiting_review", "review_points", "partner_review"].includes(e.status)).length,
        finalised: engagements.filter((e) => e.status === "finalised").length,
    };
    const byStatus = useMemo(() => {
        const m = Object.fromEntries(PIPELINE.map(([k]) => [k, 0]));
        engagements.forEach((e) => { if (e.status in m) m[e.status] += 1; });
        return m;
    }, [engagements]);
    const maxStage = Math.max(1, ...Object.values(byStatus));

    const byAgent = useMemo(() => {
        const m = {};
        engagements.forEach((e) => {
            const a = e.managing_agent || "— No managing agent —";
            if (!m[a]) m[a] = { total: 0, active: 0, awaiting_client: 0, finalised: 0 };
            m[a].total += 1;
            if (e.status === "finalised") m[a].finalised += 1; else m[a].active += 1;
            if (e.status === "awaiting_client_approval" || e.client_query_open) m[a].awaiting_client += 1;
        });
        return Object.entries(m).sort((a, b) => b[1].total - a[1].total);
    }, [engagements]);

    return (
        <div className="p-8 max-w-7xl" data-testid="dashboard-page">
            <div className="flex items-end justify-between mb-8">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display mb-1">Firm overview</div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">Jobs dashboard</h1>
                </div>
                <Link to="/engagements/new" data-testid="dashboard-new-engagement-btn" className="flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-sm">
                    <Plus className="w-4 h-4" /> New engagement
                </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <StatCard testId="stat-total" label="Total engagements" value={stats.total} icon={FolderKanban} to="/engagements" />
                <StatCard testId="stat-active" label="In progress" value={stats.active} icon={Clock} accent="amber" to="/engagements?status=active" />
                <StatCard testId="stat-review" label="Awaiting review" value={stats.review} icon={ClipboardList} accent="indigo" to="/engagements?status=review" />
                <StatCard testId="stat-finalised" label="Finalised" value={stats.finalised} icon={ShieldCheck} accent="emerald" to="/engagements?status=finalised" />
            </div>

            {/* Full workflow pipeline */}
            <div className="bg-white border border-border rounded-sm mb-8" data-testid="dashboard-pipeline">
                <div className="px-4 py-3 border-b border-border font-display font-semibold text-sm">Workflow pipeline</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 divide-x divide-border">
                    {PIPELINE.map(([key, bar]) => (
                        <Link key={key} to={`/engagements?status=${key}`} data-testid={`pipeline-${key}`} className="p-4 hover:bg-slate-50 transition-colors group">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-display leading-tight h-8 overflow-hidden">{statusLabels[key]}</div>
                            <div className="font-display font-bold text-2xl tracking-tight mt-1">{byStatus[key]}</div>
                            <div className="h-1 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${(byStatus[key] / maxStage) * 100}%` }} /></div>
                        </Link>
                    ))}
                </div>
            </div>

            <AttentionPanel items={attention} />
            <ClientQueriesPanel engagements={engagements} />

            {/* By managing agent */}
            <div className="bg-white border border-border rounded-sm mb-8" data-testid="dashboard-by-agent">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-brand" />
                    <div className="font-display font-semibold text-sm">By managing agent</div>
                </div>
                <table className="tbl">
                    <thead><tr><th>Managing agent</th><th className="text-center w-28">Total</th><th className="text-center w-28">In progress</th><th className="text-center w-36">Needs client</th><th className="text-center w-28">Finalised</th></tr></thead>
                    <tbody data-testid="dashboard-agent-rows">
                        {byAgent.map(([agent, c]) => (
                            <tr key={agent}>
                                <td className="font-medium">{agent}</td>
                                <td className="text-center font-num">{c.total}</td>
                                <td className="text-center font-num text-amber-700">{c.active}</td>
                                <td className="text-center font-num text-violet-700">{c.awaiting_client}</td>
                                <td className="text-center font-num text-emerald-700">{c.finalised}</td>
                            </tr>
                        ))}
                        {byAgent.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-8 italic">No engagements yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className="bg-white border border-border rounded-sm">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <div className="font-display font-semibold text-sm">Recent engagements</div>
                    <Link to="/engagements" className="text-xs text-brand hover:underline">View all →</Link>
                </div>
                <table className="tbl">
                    <thead><tr><th>Client</th><th>Managing agent</th><th>Type</th><th>Period</th><th>Status</th></tr></thead>
                    <tbody data-testid="dashboard-engagement-rows">
                        {engagements.slice(0, 8).map((e) => (
                            <tr key={e.id} className="cursor-pointer" onClick={() => (window.location.href = `/engagements/${e.id}`)}>
                                <td className="font-medium">{e.client_name}</td>
                                <td className="text-muted-foreground">{e.managing_agent || "—"}</td>
                                <td><span className="text-xs px-2 py-0.5 bg-slate-100 rounded-sm font-medium">{typeLabel(e.engagement_type)}</span></td>
                                <td className="font-num text-xs">{fmtPeriod(e.current_period_start, e.current_period_end)}</td>
                                <td><span className={`text-[11px] px-2 py-0.5 rounded-sm font-semibold uppercase tracking-wider ${statusStyles[e.status]}`}>{statusLabels[e.status]}</span></td>
                            </tr>
                        ))}
                        {engagements.length === 0 && (
                            <tr><td colSpan={5} className="text-center text-muted-foreground py-10">
                                No engagements yet. <Link to="/engagements/new" className="text-brand underline">Create the first one →</Link>
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function fmtPeriod(s, e) {
    if (!s || !e) return "—";
    return `${s.slice(0, 10)} → ${e.slice(0, 10)}`;
}

function AttentionPanel({ items }) {
    if (!items || items.length === 0) return null;
    const blockers = items.filter((a) => a.priority === "blocker").length;
    return (
        <div className="bg-white border border-border rounded-sm mb-8" data-testid="dashboard-attention">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <Flag className="w-4 h-4 text-orange-600" />
                <div className="font-display font-semibold text-sm">Items requiring your attention</div>
                <span className="text-[11px] text-muted-foreground">{items.length} open{blockers ? ` · ${blockers} must be resolved before draft issue` : ""}</span>
            </div>
            <div className="divide-y divide-border">
                {items.map((a) => (
                    <Link key={a.id} to={`/engagements/${a.engagement_id}/wp/${a.wp_id}`} data-testid={`dashboard-attention-row-${a.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                        {a.priority === "blocker" && <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" title="Requires resolution before draft issue" />}
                        <span className="font-num text-[11px] text-muted-foreground w-10 shrink-0">{a.wp_code}</span>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{a.title}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{a.client_name}{a.property_name ? ` · ${a.property_name}` : ""} · raised by {a.raised_by}</div>
                        </div>
                        {a.priority === "blocker"
                            ? <span className="text-[10px] font-semibold px-2 py-1 rounded-sm bg-red-600 text-white shrink-0">Blocker</span>
                            : <span className="text-[10px] font-semibold px-2 py-1 rounded-sm bg-orange-100 text-orange-800 shrink-0">Routine</span>}
                        {!a.addressed_to_me && <span className="text-[10px] text-muted-foreground shrink-0" title="You can see this as a Partner">for {a.attention_of_name || "—"}</span>}
                    </Link>
                ))}
            </div>
        </div>
    );
}

function ClientQueriesPanel({ engagements }) {
    const waiting = engagements
        .filter((e) => e.client_query_open)
        .sort((a, b) => (a.client_all_answered === b.client_all_answered ? 0 : a.client_all_answered ? 1 : -1));
    if (waiting.length === 0) return null;
    return (
        <div className="bg-white border border-border rounded-sm mb-8" data-testid="dashboard-client-queries">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <MessagesSquare className="w-4 h-4 text-brand" />
                <div className="font-display font-semibold text-sm">Files waiting on clients</div>
                <span className="text-[11px] text-muted-foreground">{waiting.length} file{waiting.length === 1 ? "" : "s"} with open client queries</span>
            </div>
            <div className="divide-y divide-border">
                {waiting.map((e) => (
                    <Link key={e.id} to={`/engagements/${e.id}`} data-testid={`dashboard-client-row-${e.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{e.client_name}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{e.managing_agent || "No managing agent"}</div>
                        </div>
                        {e.client_all_answered ? (
                            <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-sm bg-emerald-100 text-emerald-800"><Check className="w-3.5 h-3.5" /> All client replies in</span>
                        ) : (
                            <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-sm bg-amber-100 text-amber-800"><MessagesSquare className="w-3.5 h-3.5" /> Awaiting client</span>
                        )}
                    </Link>
                ))}
            </div>
        </div>
    );
}

function StatCard({ label, value, icon: Icon, accent = "slate", testId, to }) {
    const accents = { slate: "text-brand-slate", amber: "text-amber-700", indigo: "text-indigo-700", emerald: "text-emerald-700" };
    const inner = (
        <>
            <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-display">{label}</div>
                <Icon className={`w-4 h-4 ${accents[accent]}`} />
            </div>
            <div className="font-display font-bold text-3xl mt-2 tracking-tight">{value}</div>
        </>
    );
    return <Link to={to} data-testid={testId} className="bg-white border border-border rounded-sm p-4 block hover:border-brand hover:shadow-sm transition-all">{inner}</Link>;
}
